import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Layout from '../components/Layout';
import DoneModal from '../components/DoneModal';
import UploadModal from '../components/UploadModal';
import QCTicketModal from '../components/QCTicketModal';
import NotesModal from '../components/NotesModal';
import AttachmentsModal from '../components/AttachmentsModal';
import ViewMapModal from '../components/ViewMapModal';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/date';

const VERSIONS = ['V1', 'V2', 'V3', 'V4'];

const SHIPPED_BADGE = {
  loaded: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
  shipped: 'bg-green-500/20 text-green-400 border border-green-500/30',
};

export default function CADDashboard() {
  const [jobs, setJobs] = useState([]);
  const [cadUsers, setCadUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('pending'); // pending | in_progress | third_party | complete
  const [doneJob, setDoneJob] = useState(null);
  const [showDone, setShowDone] = useState(false);
  const [attachJob, setAttachJob] = useState(null);
  const [mapJob, setMapJob] = useState(null);
  const [revisitJob, setRevisitJob] = useState(null);
  const [qcJob, setQcJob] = useState(null);
  const [noteJob, setNoteJob] = useState(null);
  const [pizza, setPizza] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const readOnly = user?.role === 'upload';

  const [conflictMsg, setConflictMsg] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('all');
  const [sortField, setSortField] = useState('date');    // project | customer | program | assigned | date
  const [sortDir, setSortDir] = useState('asc');          // asc | desc — default is Date In, oldest first

  // Click a column header: same field flips direction, new field starts ascending
  function toggleSort(field) {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  }

  useEffect(() => {
    fetchJobs();
    fetchCadUsers();
    fetchPizza();
    const interval = setInterval(fetchJobsSilent, 30000);
    return () => clearInterval(interval);
  }, []);

  async function fetchPizza() {
    try { const { data } = await api.get('/jobs/pizza/tracker'); setPizza(data); } catch {}
  }

  async function fetchJobs() {
    try {
      const { data } = await api.get('/jobs');
      setJobs(data);
    } finally {
      setLoading(false);
    }
  }

  async function fetchJobsSilent() {
    try {
      const { data } = await api.get('/jobs');
      setJobs(data);
      fetchPizza();
    } catch { /* silent — don't disrupt the user */ }
  }

  async function fetchCadUsers() {
    const { data } = await api.get('/jobs/cad-users');
    setCadUsers(data);
  }

  async function handleSync() {
    setSyncing(true);
    try {
      const { data } = await api.post('/jobs/sync');
      await fetchJobs();
      alert(`Synced ${data.synced} jobs from Site Tracker`);
    } catch {
      alert('Sync failed — check your Site Tracker API config');
    } finally {
      setSyncing(false);
    }
  }

  async function handleAssign(projectId, userId, expectUnassigned = false) {
    try {
      const { data } = await api.patch(`/jobs/${projectId}/assign`, {
        userId: userId || null,
        ...(expectUnassigned && { expect_unassigned: true }),
      });
      setJobs(prev => prev.map(j => j.project_id === projectId ? { ...j, ...data } : j));
    } catch (err) {
      if (err.response?.status === 409) {
        const name = err.response.data?.acceptedBy || 'someone else';
        setConflictMsg(`Already accepted by ${name}`);
        await fetchJobsSilent();
        setTimeout(() => setConflictMsg(''), 5000);
      } else {
        alert('Failed to assign user');
      }
    }
  }

  async function patchMeta(projectId, fields) {
    try {
      const { data } = await api.patch(`/jobs/${projectId}/meta`, fields);
      setJobs(prev => prev.map(j => j.project_id === projectId ? { ...j, ...data } : j));
    } catch { alert('Update failed'); }
  }

  // Rush toggle goes through a dedicated route that upload (view-only) users may also use
  async function toggleRush(projectId, value) {
    try {
      const { data } = await api.patch(`/jobs/${projectId}/rush`, { is_rush: value });
      setJobs(prev => prev.map(j => j.project_id === projectId ? { ...j, ...data } : j));
    } catch { alert('Failed to update rush'); }
  }

  async function sendToThirdParty(projectId) {
    try {
      const { data } = await api.post(`/jobs/${projectId}/third-party`);
      setJobs(prev => prev.map(j => j.project_id === projectId ? { ...j, ...data } : j));
    } catch { alert('Update failed'); }
  }

  // Claim/release the review of a returned third-party job (required before Complete)
  async function toggleReview(projectId) {
    try {
      const { data } = await api.post(`/jobs/${projectId}/review`);
      setJobs(prev => prev.map(j => j.project_id === projectId ? { ...j, ...data } : j));
    } catch (err) {
      if (err.response?.status === 409) {
        const name = err.response.data?.reviewedBy || 'someone else';
        setConflictMsg(`Already being reviewed by ${name}`);
        await fetchJobsSilent();
        setTimeout(() => setConflictMsg(''), 5000);
      } else {
        alert('Failed to update review');
      }
    }
  }

  async function resync(projectId) {
    try {
      const { data } = await api.post(`/jobs/${projectId}/resync`);
      setJobs(prev => prev.map(j => j.project_id === projectId ? { ...j, ...data } : j));
      if (data.gis_export_synced === false || data.drafting_synced === false) {
        alert('Site Tracker still rejected the update — it may be a validation rule or a temporary issue. Try again shortly.');
      }
    } catch { alert('Resync failed'); }
  }

  function handleDoneClick(job) { setDoneJob(job); setShowDone(true); }
  async function handleDoneSuccess() { setShowDone(false); setDoneJob(null); await fetchJobs(); }

  async function saveJobNote(projectId, text) {
    await patchMeta(projectId, { notes: text });
  }

  // Summary counts
  const pending = jobs.filter(j => !j.is_done && !j.assigned_to_id && !j.is_third_party).length;
  const inProgress = jobs.filter(j => !j.is_done && j.assigned_to_id && !j.is_third_party).length;
  const rush = jobs.filter(j => !j.is_done && j.is_rush).length;
  const thirdParty = jobs.filter(j => !j.is_done && j.is_third_party).length;

  // A search looks across every status, so nobody has to re-search on each tab
  const searching = search.trim() !== '';
  const pickFilter = (key) => { setFilter(key); setSearch(''); };

  const visibleJobs = jobs.filter(j => {
    if (searching) return true;
    if (filter === 'pending')     return !j.is_done && !j.assigned_to_id && !j.is_third_party;
    if (filter === 'in_progress') return !j.is_done && j.assigned_to_id && !j.is_third_party;
    if (filter === 'third_party') return j.is_third_party && !j.is_done;
    if (filter === 'complete')    return j.is_done;
    return true;
  }).filter(j => {
    if (assignedFilter === 'all') return true;
    if (assignedFilter === 'unassigned') return !j.assigned_to_id;
    return String(j.assigned_to_id) === String(assignedFilter);
  }).filter(j =>
    [j.project_id, j.project_name, j.client_name, j.program, j.site_address]
      .join(' ').toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => {
    let cmp;
    if (sortField === 'date') {
      cmp = new Date(a.completed_date || 0) - new Date(b.completed_date || 0);
    } else {
      const val = (x) => (sortField === 'customer' ? x.client_name
        : sortField === 'program' ? x.program
        : sortField === 'assigned' ? x.assigned_to_name
        : x.project_id);
      cmp = String(val(a) || '').localeCompare(String(val(b) || ''), undefined, { numeric: true, sensitivity: 'base' });
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white tracking-widest uppercase">CAD Dashboard</h1>
            <p className="text-gray-400 text-sm mt-1">Field Complete — Pending GIS Export{readOnly && ' · View only'}</p>
          </div>
          {!readOnly && (
            <button
              onClick={handleSync}
              disabled={syncing}
              className="px-4 py-2 text-xs font-bold uppercase tracking-widest rounded border transition disabled:opacity-50"
              style={{ borderColor: '#c9a84c', color: '#c9a84c' }}
            >
              {syncing ? 'Syncing…' : 'Sync from Site Tracker'}
            </button>
          )}
        </div>

        {/* Pizza-party tracker */}
        {pizza && (
          <div className="rounded-xl border p-4 flex items-center gap-4" style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}>
            <div className="text-3xl leading-none">🍕</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1.5 gap-3">
                <span className="text-sm font-bold text-white">
                  {pizza.remaining <= 25 ? '🔥 Almost pizza time!' : 'Next Pizza Party'}
                </span>
                <span className="text-xs text-gray-400 shrink-0">
                  {pizza.into_cycle.toLocaleString()} / {pizza.goal.toLocaleString()} ·{' '}
                  <span className="font-bold" style={{ color: '#c9a84c' }}>{pizza.remaining.toLocaleString()} to go</span>
                  {pizza.parties > 0 && <span className="text-gray-500"> · 🎉 {pizza.parties} earned</span>}
                </span>
              </div>
              <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: '#0d1b2e' }}>
                <div className="h-3 rounded-full transition-all" style={{ width: `${(pizza.into_cycle / pizza.goal) * 100}%`, backgroundColor: '#c9a84c' }} />
              </div>
            </div>
          </div>
        )}

        {/* Conflict banner */}
        {conflictMsg && (
          <div className="rounded-lg px-4 py-3 text-sm font-semibold text-white flex items-center justify-between"
               style={{ backgroundColor: '#7f1d1d', border: '1px solid #ef4444' }}>
            <span>⚠ {conflictMsg} — list refreshed</span>
            <button onClick={() => setConflictMsg('')} className="text-white/60 hover:text-white ml-4">✕</button>
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Pending', value: pending, color: 'text-yellow-400', key: 'pending' },
            { label: 'In Progress', value: inProgress, color: 'text-blue-400', key: 'in_progress' },
            { label: 'Rush', value: rush, color: 'text-red-400', key: 'in_progress' },
            { label: '3rd Party', value: thirdParty, color: 'text-purple-400', key: 'third_party' },
          ].map(card => (
            <button
              key={card.label}
              onClick={() => pickFilter(card.key)}
              className="border rounded-lg p-4 text-left transition hover:border-gold-500"
              style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}
            >
              <p className={`text-3xl font-black ${card.color}`}>{card.value}</p>
              <p className="text-xs text-gray-400 uppercase tracking-widest mt-1">{card.label}</p>
            </button>
          ))}
        </div>

        {/* Filter tabs + search */}
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            {[
              { key: 'pending',     label: 'Pending' },
              { key: 'in_progress', label: 'In Progress' },
              { key: 'third_party', label: '3rd Party' },
              { key: 'complete',    label: 'Complete' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => pickFilter(f.key)}
                className={`px-3 py-1 rounded text-xs font-bold uppercase tracking-widest transition ${
                  filter === f.key && !searching ? 'text-navy-900' : 'text-gray-400 hover:text-white'
                }`}
                style={filter === f.key && !searching ? { backgroundColor: '#c9a84c', color: '#0d1b2e' } : {}}
              >
                {f.label}
              </button>
            ))}
          </div>
          {/* Assigned filter */}
          <select
            value={assignedFilter}
            onChange={e => setAssignedFilter(e.target.value)}
            className="border rounded px-2 py-1.5 text-xs text-white focus:outline-none"
            style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
          >
            <option value="all">All Assignees</option>
            <option value="unassigned">Unassigned</option>
            {cadUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>

          <input
            type="text"
            placeholder="Search all statuses…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 border rounded px-4 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none transition"
            style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
          />
        </div>

        {searching && !loading && (
          <p className="text-xs text-gray-400">
            Searching Pending, In Progress, 3rd Party and Complete · {visibleJobs.length} match{visibleJobs.length === 1 ? '' : 'es'}
          </p>
        )}

        {/* Jobs table */}
        {loading ? (
          <p className="text-gray-500 text-sm">Loading jobs…</p>
        ) : visibleJobs.length === 0 ? (
          <p className="text-gray-500 text-sm">{searching ? 'No jobs match that search.' : 'No jobs. Try syncing from Site Tracker.'}</p>
        ) : (
          <div className="rounded-xl border overflow-x-auto" style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}>
            <table className="w-full text-sm">
              <thead className="border-b" style={{ backgroundColor: '#080f1a', borderColor: '#1e3a5f' }}>
                <tr>
                  {[
                    { label: '' },
                    { label: 'Project ID', field: 'project' },
                    { label: 'Client', field: 'customer' },
                    { label: 'Program', field: 'program' },
                    { label: 'Job Name' },
                    { label: 'Ver' },
                    { label: 'Assigned To', field: 'assigned' },
                    { label: 'Date In', field: 'date' },
                    { label: 'Status' },
                    { label: 'Files' },
                    { label: 'Notes' },
                    { label: '' },
                  ].map((h, i) => (
                    <th key={i} className="text-left px-3 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      {h.field ? (
                        <button onClick={() => toggleSort(h.field)} className="flex items-center gap-1 uppercase tracking-wide hover:text-gold-400 transition">
                          {h.label}
                          <span className="text-gold-500">{sortField === h.field ? (sortDir === 'asc' ? '▲' : '▼') : ''}</span>
                        </button>
                      ) : h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: '#1e3a5f' }}>
                {visibleJobs.map(job => {
                  const statusLabel = job.is_done
                    ? job.loaded_shipped ? job.loaded_shipped.toUpperCase() : 'COMPLETE'
                    : job.is_third_party ? '3RD PARTY'
                    : job.assigned_to_id ? 'IN PROGRESS'
                    : 'PENDING';

                  const statusColor = job.is_done
                    ? 'text-green-400'
                    : job.is_third_party ? 'text-purple-400'
                    : job.assigned_to_id ? 'text-blue-400'
                    : 'text-yellow-400';

                  return (
                    <tr key={job.project_id} className={`transition hover:bg-white/5 ${job.is_rush ? 'border-l-2 border-red-500' : ''}`}>

                      {/* Rush toggle */}
                      <td className="px-2 py-3 w-8">
                        <button
                          title={job.is_rush ? 'Remove rush' : 'Mark rush'}
                          onClick={() => toggleRush(job.project_id, !job.is_rush)}
                          className={`text-sm transition ${job.is_rush ? 'text-red-400' : 'text-gray-600 hover:text-red-400'}`}
                        >
                          ⚡
                        </button>
                      </td>

                      {/* Project ID */}
                      <td className="px-3 py-3 font-mono text-xs text-gray-400 whitespace-nowrap">{job.project_id}</td>

                      {/* Client */}
                      <td className="px-3 py-3 text-gray-300 text-xs uppercase">{job.client_name || '—'}</td>

                      {/* Program (from Site Tracker) */}
                      <td className="px-3 py-3 text-gray-400 text-xs whitespace-nowrap">{job.program || '—'}</td>

                      {/* Job Name */}
                      <td className="px-3 py-3 text-white font-medium text-xs">{job.project_name || '—'}</td>

                      {/* Version */}
                      <td className="px-3 py-3">
                        {readOnly ? (
                          <span className="text-xs" style={{ color: job.version ? '#c9a84c' : '#6b7280' }}>{job.version || '—'}</span>
                        ) : (
                          <select
                            value={job.version || ''}
                            onChange={e => patchMeta(job.project_id, { version: e.target.value })}
                            className="text-xs rounded px-1.5 py-1 border focus:outline-none"
                            style={{ backgroundColor: '#142236', borderColor: '#1e3a5f', color: job.version ? '#c9a84c' : '#6b7280' }}
                          >
                            <option value="">—</option>
                            {VERSIONS.map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                        )}
                      </td>

                      {/* Assigned To */}
                      <td className="px-3 py-3">
                        {job.is_third_party ? (
                          <>
                            <span className="text-xs text-purple-400 font-medium">Third Party</span>
                            {/* The reviewer is the only CAD tech who touches a 3rd-party job, so
                                credit them here — this is the only place it shows once the job
                                is done and drops out of the 3rd Party tab. */}
                            {job.reviewed_by_name && (
                              <span className="block text-[10px] text-gray-500 mt-0.5">
                                {job.is_done ? 'Reviewed by' : 'In review by'} {job.reviewed_by_name}
                              </span>
                            )}
                          </>
                        ) : readOnly ? (
                          <span className="text-xs text-gray-300">{job.assigned_to_name || '—'}</span>
                        ) : (
                          <>
                            {!job.is_done && job.assigned_to_id && (
                              <select
                                value={job.assigned_to_id || ''}
                                onChange={e => handleAssign(job.project_id, e.target.value)}
                                className="text-xs rounded px-1.5 py-1 border focus:outline-none"
                                style={{ backgroundColor: '#142236', borderColor: '#1e3a5f', color: '#fff' }}
                              >
                                <option value="">Unassigned</option>
                                {cadUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                              </select>
                            )}
                            {(!job.assigned_to_id && !job.is_done) && <span className="text-xs text-gray-600">—</span>}
                            {job.is_done && <span className="text-xs text-gray-500">{job.assigned_to_name || '—'}</span>}
                          </>
                        )}
                      </td>

                      {/* Date In */}
                      <td className="px-3 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {fmtDate(job.completed_date)}
                      </td>

                      {/* Status */}
                      <td className="px-3 py-3">
                        <span className={`text-xs font-bold uppercase tracking-wide ${statusColor}`}>{statusLabel}</span>
                        {(job.gis_export_synced === false || job.drafting_synced === false) && (
                          <button
                            onClick={() => resync(job.project_id)}
                            title="A Site Tracker date stamp failed to save — click to retry"
                            className="block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded border border-red-500/50 text-red-400 hover:bg-red-500/10 transition"
                          >
                            ⚠ Not synced · Retry
                          </button>
                        )}
                      </td>

                      {/* Attachments */}
                      <td className="px-3 py-3">
                        <button
                          onClick={() => setAttachJob(job)}
                          title="View Site Tracker attachments"
                          className="text-xs px-2 py-1 rounded border transition text-gray-400 hover:text-gold-400"
                          style={{ borderColor: '#1e3a5f' }}
                        >
                          📎 Files
                        </button>
                      </td>

                      {/* Notes */}
                      <td className="px-3 py-3 max-w-[200px]">
                        <button
                          onClick={() => setNoteJob(job)}
                          title={job.notes || (readOnly ? 'No note' : 'Add a note')}
                          className={`text-xs text-left truncate max-w-full transition ${job.notes ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-400'}`}
                        >
                          {job.notes
                            ? (job.notes.length > 28 ? `${job.notes.slice(0, 28)}…` : job.notes)
                            : (readOnly ? '—' : '+ note')}
                        </button>
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          {!readOnly && !job.is_done && !job.is_third_party && !job.assigned_to_id && (
                            <>
                              <button
                                onClick={() => handleAssign(job.project_id, user.id, true)}
                                className="text-xs px-2 py-1 rounded font-bold uppercase tracking-wide transition"
                                style={{ backgroundColor: '#1e3a5f', color: '#c9a84c', border: '1px solid #c9a84c' }}
                              >
                                Accept
                              </button>
                              <button
                                onClick={() => sendToThirdParty(job.project_id)}
                                className="text-xs px-2 py-1 rounded border transition text-purple-400 border-purple-500/40 hover:bg-purple-500/10"
                                title="Send to 3rd party"
                              >
                                3P
                              </button>
                            </>
                          )}
                          {!readOnly && !job.is_done && !job.is_third_party && job.assigned_to_id && (
                            <>
                              <button
                                onClick={() => setMapJob(job)}
                                className="text-xs px-2 py-1 rounded border transition text-blue-300 border-blue-500/40 hover:bg-blue-500/10"
                                title="View map & export"
                              >
                                🗺️
                              </button>
                              <button
                                onClick={() => setQcJob(job)}
                                className="text-xs px-2 py-1 rounded border transition text-green-300 border-green-500/40 hover:bg-green-500/10"
                                title="Start a QC ticket"
                              >
                                QC
                              </button>
                              <button
                                onClick={() => sendToThirdParty(job.project_id)}
                                className="text-xs px-2 py-1 rounded border transition text-purple-400 border-purple-500/40 hover:bg-purple-500/10"
                                title="Send to 3rd party"
                              >
                                3P
                              </button>
                              <button
                                onClick={() => handleDoneClick(job)}
                                className="text-xs px-2 py-1 rounded font-bold uppercase tracking-wide transition"
                                style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
                              >
                                Done
                              </button>
                            </>
                          )}
                          {!readOnly && job.is_third_party && !job.is_done && (
                            <>
                              <button
                                onClick={() => patchMeta(job.project_id, { third_party_received: !job.third_party_received })}
                                className={`text-xs px-2 py-1 rounded font-bold uppercase tracking-wide border transition min-w-[5.5rem] text-center ${
                                  job.third_party_received
                                    ? 'text-green-400 border-green-500/50 bg-green-500/10 hover:bg-green-500/20'
                                    : 'text-yellow-400 border-yellow-500/50 bg-yellow-500/10 hover:bg-yellow-500/20'
                                }`}
                                title={job.third_party_received ? 'Received back from 3rd party — click to mark as still out' : 'Sent to 3rd party — click when the job comes back'}
                              >
                                {job.third_party_received ? 'Received' : 'Sent'}
                              </button>
                              {job.third_party_received && (
                                <button
                                  onClick={() => toggleReview(job.project_id)}
                                  className={`text-xs px-2 py-1 rounded font-bold uppercase tracking-wide border transition ${
                                    job.reviewed_by_id
                                      ? 'text-purple-300 border-purple-500/50 bg-purple-500/10 hover:bg-purple-500/20'
                                      : 'text-white border-purple-400 bg-purple-500/40 hover:bg-purple-500/60'
                                  }`}
                                  title={job.reviewed_by_id
                                    ? `Being reviewed by ${job.reviewed_by_name} — click to release`
                                    : 'Claim the review of this returned job (required before Complete)'}
                                >
                                  {job.reviewed_by_id ? `Reviewing · ${job.reviewed_by_name}` : 'Review'}
                                </button>
                              )}
                              <button
                                onClick={() => setMapJob(job)}
                                className="text-xs px-2 py-1 rounded border transition text-blue-300 border-blue-500/40 hover:bg-blue-500/10"
                                title="View map & export"
                              >
                                🗺️
                              </button>
                              <button
                                onClick={() => setQcJob(job)}
                                className="text-xs px-2 py-1 rounded border transition text-green-300 border-green-500/40 hover:bg-green-500/10"
                                title="Start a QC ticket"
                              >
                                QC
                              </button>
                              <button
                                onClick={() => handleDoneClick(job)}
                                disabled={!job.reviewed_by_id}
                                className="text-xs px-2 py-1 rounded font-bold uppercase tracking-wide transition disabled:opacity-40 disabled:cursor-not-allowed"
                                style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
                                title={job.reviewed_by_id ? undefined : 'A CAD tech must claim the review before this job can be completed'}
                              >
                                Complete
                              </button>
                              <button
                                onClick={() => patchMeta(job.project_id, { is_third_party: false })}
                                className="text-xs px-2 py-1 rounded border text-gray-400 hover:text-white transition"
                                style={{ borderColor: '#1e3a5f' }}
                              >
                                Recall
                              </button>
                            </>
                          )}
                          {job.is_done && (
                            <>
                              <span className="text-xs text-green-400 font-bold">✓ Complete</span>
                              {!readOnly && (
                                <>
                                  <button
                                    onClick={() => setMapJob(job)}
                                    className="text-xs px-2 py-1 rounded border transition text-blue-300 border-blue-500/40 hover:bg-blue-500/10"
                                    title="View map & export"
                                  >
                                    🗺️
                                  </button>
                                  <button
                                    onClick={() => setRevisitJob(job)}
                                    className="text-xs px-2 py-1 rounded border transition text-amber-300 border-amber-500/40 hover:bg-amber-500/10"
                                    title="Upload additional documents (revisit)"
                                  >
                                    ↻ Revisit
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {qcJob && (
        <QCTicketModal
          job={qcJob}
          onClose={() => setQcJob(null)}
          onCreated={() => { setQcJob(null); navigate('/qc'); }}
        />
      )}

      {noteJob && (
        <NotesModal
          title={noteJob.project_id}
          subtitle={noteJob.client_name}
          note={noteJob.notes}
          readOnly={readOnly}
          onClose={() => setNoteJob(null)}
          onSave={(text) => saveJobNote(noteJob.project_id, text)}
        />
      )}

      {revisitJob && (
        <UploadModal
          uploadUrl={`/jobs/${revisitJob.project_id}/upload`}
          title={revisitJob.project_id}
          subtitle={revisitJob.project_name}
          heading="Revisit — Upload Files"
          onClose={() => setRevisitJob(null)}
          onUploaded={() => { setRevisitJob(null); fetchJobs(); }}
        />
      )}

      {showDone && doneJob && (
        <DoneModal
          job={doneJob}
          onClose={() => { setShowDone(false); setDoneJob(null); }}
          onSuccess={handleDoneSuccess}
        />
      )}

      {attachJob && (
        <AttachmentsModal
          title={attachJob.project_id}
          subtitle={attachJob.project_name}
          listUrl={`/jobs/${attachJob.project_id}/attachments`}
          downloadUrl={(id) => `/jobs/${attachJob.project_id}/attachments/${id}/download`}
          onClose={() => setAttachJob(null)}
        />
      )}

      {mapJob && (
        <ViewMapModal job={mapJob} onClose={() => setMapJob(null)} />
      )}
    </Layout>
  );
}
